import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "./trpc.js";

export type Stage = "regular" | "playoff" | "tournament" | "all";
export type Span = "season" | "allTime";

/**
 * What a page is showing: which competition, which stage, and whether it is
 * scoped to one season or to the whole league.
 *
 * All three live in the URL. The season already did; scope and stage were
 * component state, so "all-time playoff records" was a view you could reach but
 * not link to, and your choice reset every time you moved between pages that
 * ask the same question.
 *
 * Each page still chooses its own defaults, because the honest default differs:
 * a franchise's record is a cross-season fact, while blending a bracket run
 * into a season's stat leaders flatters whoever went deepest.
 */
export function useScope(defaults: { span?: Span; stage?: Stage } = {}) {
  const trpc = useTRPC();
  const [params, setParams] = useSearchParams();

  const seasons = useQuery(
    trpc.league.seasons.queryOptions(undefined, { staleTime: 60_000 }),
  );
  const list = seasons.data ?? [];

  const STAGES: Stage[] = ["regular", "playoff", "tournament", "all"];
  const SPANS: Span[] = ["season", "allTime"];

  /**
   * Read a URL parameter as one of a known set, falling back when it isn't.
   *
   * These used to be cast straight out of the URL. A value that wasn't a real
   * stage — a typo, an old bookmark from before a rename — went through to the
   * server, failed its enum check, and the raw Zod error rendered on the page
   * as a wall of JSON.
   */
  const oneOf = <T extends string>(key: string, allowed: T[], fallback: T): T => {
    const raw = params.get(key);
    return raw != null && (allowed as string[]).includes(raw) ? (raw as T) : fallback;
  };

  const slug = params.get("season");
  const span = oneOf("span", SPANS, defaults.span ?? "season");
  const stage = oneOf("stage", STAGES, defaults.stage ?? "all");

  /** Writing a default back to the URL would clutter it; drop those keys. */
  const set = (key: string, value: string | null, fallback: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value == null || value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  // With no ?season the server resolves whichever season is marked current, so
  // the dropdown has to resolve it the same way — otherwise the moment the
  // Commissioner moves `isCurrent`, the page shows one season and the picker
  // names another.
  const currentSlug = list.find((s) => s.isCurrent)?.slug ?? list[0]?.slug;

  return {
    /** undefined means "let the server pick the current season" */
    slug: slug ?? undefined,
    /** what is actually on screen — the URL's season, else the current one */
    resolvedSlug: slug ?? currentSlug,
    setSeason: (v: string | null) => set("season", v, undefined),
    span,
    setSpan: (v: Span) => set("span", v, defaults.span ?? "season"),
    stage,
    setStage: (v: Stage) => set("stage", v, defaults.stage ?? "all"),
    /** what to send to an endpoint taking `stage` */
    stageParam: stage === "all" ? null : stage,
    seasons: list,
    isLoading: seasons.isLoading,
  };
}
